import React from 'react';
import { cn } from '@core/lib/utils';
import { useChatCopy } from '../../config';
import type { ChipsPayload, ChipOption } from '../services/stevensV4Service';

/**
 * #1308 (CP-21, C-8): agent-offered suggestion chips for the next user turn.
 *
 * Contract (PRD C-8 + the NN/g quality bar, enforced by useStevensV4):
 * - Rendered only while an un-consumed `ui_chips` offering is standing; the
 *   hook clears it on every send (per-turn regeneration) and filters options
 *   the user has already seen (never resurface declined options).
 * - Tap SUBMITS the option’s label as a normal user turn â it never records
 *   anything on its own.
 * - The visible label IS the accessible name (WCAG SC 2.5.3, voice-control
 *   users): no aria-label overrides on the option buttons.
 */
interface SuggestionChipsProps {
  chips: ChipsPayload;
  onSelect: (option: ChipOption) => void;
  /** Disable taps while a stream is active (the send would be dropped). */
  disabled?: boolean;
  className?: string;
}

export const SuggestionChips = React.memo(function SuggestionChips({
  chips,
  onSelect,
  disabled = false,
  className,
}: SuggestionChipsProps) {
  // #2109 R3-B24: the hook subscribes through React.memo to a language switch.
  const copy = useChatCopy();
  if (chips.options.length === 0) return null;

  return (
    <div
      role="group"
      aria-label={copy.chips.regionLabel}
      className={cn('flex flex-wrap gap-2', className)}
    >
      {chips.options.map((option) => (
        <button
          key={option.id}
          type="button"
          disabled={disabled}
          onClick={() => onSelect(option)}
          title={option.descriptor}
          className={cn(
            // #1303 preset-contract keys (§14.5: chip-suggest fill, signal
            // border â visibly the agent’s offering, still quiet).
            'min-h-[44px] rounded-chip border border-signal bg-chat-chip-suggest px-4 py-1.5',
            'text-body-sm text-chat-ink transition-colors',
            'hover:border-chat-accent focus:outline-none focus:ring-2 focus:ring-focus-outer',
            'disabled:pointer-events-none disabled:opacity-50'
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
});
